/**
 * GoalOverlayLayer
 *
 * A canvas overlay that renders per-goal-type visual indicators on top of the
 * athlete video.  It is designed to sit absolutely positioned over the video
 * element, is pointer-events:none, and will be wired into EnhancedEvaluationDisplay
 * in a later step.
 *
 * Draw function responsibilities:
 *  - drawAxisGoal        — colored axis line between two landmarks + deviation arc
 *  - drawTimingGoal      — rings around A/B landmarks + event flash + dashed connector
 *  - drawSpatialGoal     — pass/fail badge at an offset position (no landmarks in result)
 *  - drawMovementGoal    — trajectory polyline up to current frame + current-position dot
 *  - drawJointAngleGoal  — pass/fail badge (no landmark info in result)
 */

import { useEffect, useRef } from 'react';
import type { SpatialGoalResult, AxisGoalResult, TimingGoalResult, RawPoseFrame } from '@/types/api';
import { GOAL_OVERLAY } from '@/utils/visualizationConstants';
import {
  classifyGoalKey,
  getLandmarkPosition,
  getAnchorPosition,
  scoreToColor,
  drawScoreBadge,
} from '@/utils/goalOverlayHelpers';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface GoalOverlayLayerProps {
  /** Keyed goal results from EvaluationResult.spatial_goal_results */
  goalResults: Record<string, SpatialGoalResult | AxisGoalResult | TimingGoalResult>;
  /** Raw pose data array (one element per frame, each with a `landmarks` array) */
  poseData: RawPoseFrame[] | undefined;
  /** Current video frame index (0-based) */
  currentFrame: number;
  /** Canvas / video display width in pixels */
  width: number;
  /** Canvas / video display height in pixels */
  height: number;
  /** Whether the athlete video is mirrored relative to the template */
  mirrored: boolean;
  /** Master toggle — when false the canvas is cleared and nothing is drawn */
  enabled: boolean;
  /**
   * When true, score badges and athlete-specific deviation indicators are
   * suppressed. Use this on the template (coach) video panel so that only
   * the geometric reference lines are drawn without athlete score values.
   * Spatial/movement/joint-angle goals (which are pure pass/fail badges)
   * are skipped entirely in this mode since they carry no geometric value
   * on the template video.
   * In anchor mode, per_frame_anchor positions are bypassed and landmark
   * names are used instead, because anchor positions were computed from
   * the athlete's (potentially mirrored) pose data.
   */
  hideScores?: boolean;
  /**
   * The underlying HTMLVideoElement. When provided the overlay uses the
   * video's intrinsic dimensions to compute letterbox offsets so that goal
   * indicators align with the actual rendered video area rather than the
   * full container box.
   */
  videoElement?: HTMLVideoElement | null;
  /**
   * The demo's own travel path for a point being authored as a travel
   * target (travel/preview-path-points, `docs/coach_input/
   * TRAVEL_PR_A_CONTRACT.md` §2.1) — `Array<[frame, x, y]>`, the preview
   * endpoint's `trajectory_points`. Drawn with the SAME polyline+dot
   * primitive `drawMovementGoal` uses for a scored result (see
   * `drawTrajectoryPath` below), just with no score badge and a fixed
   * "demo" color instead of a pass/fail one. Independent of `goalResults`
   * — this is authoring-time data, not a scored result — so it draws
   * whenever non-empty and `enabled` is true, regardless of `hideScores`.
   * `null`/undefined/empty draws nothing.
   */
  previewTrajectory?: Array<[number, number, number]> | null;
}

// ---------------------------------------------------------------------------
// Letterbox helper
// ---------------------------------------------------------------------------

/**
 * Compute the actual rendered area of the video within its container,
 * accounting for aspect ratio differences (letterboxing).
 * Returns { offsetX, offsetY, renderWidth, renderHeight }.
 */
function computeVideoRenderRect(
  containerWidth: number,
  containerHeight: number,
  videoElement?: HTMLVideoElement | null,
): { offsetX: number; offsetY: number; renderWidth: number; renderHeight: number } {
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    // No video intrinsic size available — assume no letterboxing
    return { offsetX: 0, offsetY: 0, renderWidth: containerWidth, renderHeight: containerHeight };
  }

  const videoAspect = videoElement.videoWidth / videoElement.videoHeight;
  const containerAspect = containerWidth / containerHeight;

  let renderWidth: number;
  let renderHeight: number;
  let offsetX: number;
  let offsetY: number;

  if (videoAspect > containerAspect) {
    // Video is wider — letterbox top/bottom
    renderWidth = containerWidth;
    renderHeight = containerWidth / videoAspect;
    offsetX = 0;
    offsetY = (containerHeight - renderHeight) / 2;
  } else {
    // Video is taller — letterbox left/right
    renderHeight = containerHeight;
    renderWidth = containerHeight * videoAspect;
    offsetX = (containerWidth - renderWidth) / 2;
    offsetY = 0;
  }

  return { offsetX, offsetY, renderWidth, renderHeight };
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

function isAxisGoalResult(
  r: SpatialGoalResult | AxisGoalResult | TimingGoalResult | null | undefined,
): r is AxisGoalResult {
  return r != null && 'alignment_score' in r;
}

function isTimingGoalResult(
  r: SpatialGoalResult | AxisGoalResult | TimingGoalResult | null | undefined,
): r is TimingGoalResult {
  return r != null && 'timing_score' in r && !('alignment_score' in r);
}

// ---------------------------------------------------------------------------
// Utility: check if current frame is within the goal's evaluated range
// ---------------------------------------------------------------------------

/**
 * Returns true if `frameIndex` is within the `valid_frame_indices` array.
 * Uses min/max range check for performance instead of indexOf on every frame.
 * Falls back to true (always draw) if no valid_frame_indices are provided.
 *
 * NOTE: This check is intentionally a min/max range and not an exact Set lookup.
 * For anchor mode, this can produce false positives when valid_frame_indices is
 * sparse (e.g., visibility gaps). In anchor mode, getAnchorPosition() returning
 * null is the authoritative safety net — the early-return null check below is
 * what actually prevents drawing on invalid frames in anchor mode.
 */
function isFrameInRange(
  validFrameIndices: number[] | undefined,
  frameIndex: number,
): boolean {
  if (!validFrameIndices || validFrameIndices.length === 0) return true;
  const minFrame = validFrameIndices[0];
  const maxFrame = validFrameIndices[validFrameIndices.length - 1];
  return frameIndex >= minFrame && frameIndex <= maxFrame;
}

// ---------------------------------------------------------------------------
// Draw: Axis Goal
// ---------------------------------------------------------------------------

function drawAxisGoal(
  ctx: CanvasRenderingContext2D,
  _key: string,
  result: AxisGoalResult,
  poseData: RawPoseFrame[] | undefined,
  frameIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
  mirrored: boolean,
  hideScores: boolean,
): void {
  let sx: number;
  let sy: number;
  let ex: number;
  let ey: number;

  // When hideScores=true this overlay is rendering on the template (coach) video.
  // per_frame_anchor positions were computed from the athlete's (possibly mirrored)
  // poses — they belong to the athlete video. Skip them here and fall through to
  // the landmark fallback path, which uses the template's own pose data.
  if (result.per_frame_anchor_start != null && !hideScores) {
    // Per-frame positions mode (anchor, virtual, or standard landmark).
    // Positions are computed from potentially-mirrored poses in the backend.
    // When mirrored=true, X coordinates need to be flipped to display on
    // the original (un-mirrored) video.
    const anchorStart = getAnchorPosition(
      result.per_frame_anchor_start,
      result.valid_frame_indices,
      frameIndex,
    );
    const anchorEnd = getAnchorPosition(
      result.per_frame_anchor_end,
      result.valid_frame_indices,
      frameIndex,
    );

    if (!anchorStart || !anchorEnd) {
      // Frame is not in validFrameIndices (e.g. viewing frame 0 while the goal
      // only covers frames 30-153) — this is normal; silently skip drawing.
      // Warn (in all environments) only when the frame IS supposedly valid but
      // the anchor data is missing — that indicates a backend data-shape problem.
      const frameIsValid = result.valid_frame_indices != null &&
        result.valid_frame_indices.indexOf(frameIndex) !== -1;
      if (frameIsValid) {
        console.warn(`[GoalOverlayLayer] Anchor lookup failed for in-range frame ${frameIndex}:`, {
          anchorStart, anchorEnd,
          perFrameStartLen: result.per_frame_anchor_start?.length,
          perFrameEndLen: result.per_frame_anchor_end?.length,
          validFrameIndicesLen: result.valid_frame_indices?.length,
        });
      }
      return;
    }

    const startX = mirrored ? 1.0 - anchorStart.x : anchorStart.x;
    const endX   = mirrored ? 1.0 - anchorEnd.x   : anchorEnd.x;
    sx = startX * renderWidth + offsetX;
    sy = anchorStart.y * renderHeight + offsetY;
    ex = endX * renderWidth + offsetX;
    ey = anchorEnd.y * renderHeight + offsetY;
  } else {
    // Landmark fallback — guard with isFrameInRange before landmark lookup.
    // effective_landmark_* names are PHYSICAL body part names (the backend
    // already applied mirror_landmark_name). Pass mirrored=false to avoid
    // a double-swap (same class as LFT-021).
    if (!isFrameInRange(result.valid_frame_indices, frameIndex)) return;

    const startName = result.effective_landmark_start;
    const endName   = result.effective_landmark_end;

    if (!startName || !endName) return;

    const startPos = getLandmarkPosition(poseData, frameIndex, startName, false);
    const endPos   = getLandmarkPosition(poseData, frameIndex, endName,   false);

    if (!startPos || !endPos) return;

    sx = startPos.x * renderWidth + offsetX;
    sy = startPos.y * renderHeight + offsetY;
    ex = endPos.x * renderWidth + offsetX;
    ey = endPos.y * renderHeight + offsetY;
  }
  const mx = (sx + ex) / 2;
  const my = (sy + ey) / 2;

  // ─── Axis visual: "Surveyor's Line" — always amber, dashed ──────────
  // The axis line uses a fixed amber color to be instantly distinguishable
  // from the green body region hulls. The score badge uses standard colors.
  const AXIS_COLOR = '#F5A524';
  const badgeColor = scoreToColor(result.alignment_score);

  // 1. Subtle glow underlay for visibility on all backgrounds
  ctx.save();
  ctx.strokeStyle = AXIS_COLOR;
  ctx.shadowColor = AXIS_COLOR;
  ctx.shadowBlur  = 12;
  ctx.lineWidth   = 6;
  ctx.globalAlpha = 0.25;
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.restore();

  // 2. Main dashed amber line
  ctx.save();
  ctx.strokeStyle = AXIS_COLOR;
  ctx.lineWidth   = 2.5;
  ctx.globalAlpha = 0.9;
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.moveTo(sx, sy);
  ctx.lineTo(ex, ey);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // 3. Diamond endpoints (distinct from timing goal circles)
  [{ x: sx, y: sy }, { x: ex, y: ey }].forEach(({ x: dx, y: dy }) => {
    ctx.save();
    ctx.fillStyle   = AXIS_COLOR;
    ctx.shadowColor = AXIS_COLOR;
    ctx.shadowBlur  = 6;
    ctx.globalAlpha = 0.95;
    ctx.translate(dx, dy);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-4, -4, 8, 8);
    ctx.restore();
  });

  // 4. Deviation whisker at midpoint (perpendicular dashed line, score-colored)
  // Suppressed on the template video — deviation is an athlete-specific measurement.
  if (!hideScores) {
    const deviation = getPerFrameValue(result.per_frame_deviations, result.valid_frame_indices, frameIndex);
    if (deviation !== null && deviation > 2) {
      const axisAngle = Math.atan2(ey - sy, ex - sx);
      const perpAngle = axisAngle + Math.PI / 2;
      const whiskerLen = Math.min(deviation * 0.8, 30);

      ctx.save();
      ctx.strokeStyle = badgeColor;
      ctx.lineWidth   = 1.5;
      ctx.globalAlpha = 0.6;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(mx - Math.cos(perpAngle) * whiskerLen, my - Math.sin(perpAngle) * whiskerLen);
      ctx.lineTo(mx + Math.cos(perpAngle) * whiskerLen, my + Math.sin(perpAngle) * whiskerLen);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // 5. Score badge at midpoint (athlete score — suppressed on template video)
    drawScoreBadge(ctx, mx, my - 16, result.alignment_score);
  }
}

// ---------------------------------------------------------------------------
// Draw: Timing Goal
// ---------------------------------------------------------------------------

function drawTimingGoal(
  ctx: CanvasRenderingContext2D,
  _key: string,
  result: TimingGoalResult,
  poseData: RawPoseFrame[] | undefined,
  frameIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
  mirrored: boolean,
  hideScores: boolean,
): void {
  // Only draw during frames the goal was evaluated for
  if (!isFrameInRange(result.valid_frame_indices, frameIndex)) return;

  // Withheld measurement (viewpoint Phase 1): `timing_score === null` means the
  // evaluator abstained, NOT that the athlete scored 0. The dispatcher already
  // skips `status === 'abstained'`; this second check is what lets the colour
  // and badge below take a plain `number`, and it also covers a payload that
  // nulls the score without setting the status.
  if (result.timing_score === null) return;

  const nameA = result.effective_landmark_a;
  const nameB = result.effective_landmark_b;

  if (!nameA || !nameB) return;

  // effective_landmark_* names are PHYSICAL body part names — the backend
  // already applied mirror_landmark_name(). Pass mirrored=false to avoid
  // double-swap (same class as LFT-021: index swap + X flip on already-
  // physical names produces wrong positions when limbs aren't symmetric).
  const posA = getLandmarkPosition(poseData, frameIndex, nameA, false);
  const posB = getLandmarkPosition(poseData, frameIndex, nameB, false);

  if (!posA || !posB) return;

  const ax = posA.x * renderWidth + offsetX;
  const ay = posA.y * renderHeight + offsetY;
  const bx = posB.x * renderWidth + offsetX;
  const by = posB.y * renderHeight + offsetY;
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;

  const color = scoreToColor(result.timing_score);

  // Dashed connector between the two landmarks
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth   = 2;
  ctx.globalAlpha = 0.65;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(bx, by);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();

  // Rings around each landmark
  const nearEventA = result.landmark_a_event_frame !== undefined &&
    Math.abs(frameIndex - result.landmark_a_event_frame) <= GOAL_OVERLAY.TIMING_EVENT_WINDOW;
  const nearEventB = result.landmark_b_event_frame !== undefined &&
    Math.abs(frameIndex - result.landmark_b_event_frame) <= GOAL_OVERLAY.TIMING_EVENT_WINDOW;

  drawTimingRing(ctx, ax, ay, color, nearEventA);
  drawTimingRing(ctx, bx, by, color, nearEventB);

  // Score badge at midpoint — suppressed on the template video
  if (!hideScores) {
    drawScoreBadge(ctx, mx, my - 18, result.timing_score);
  }
}

function drawTimingRing(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  color: string,
  isEvent: boolean,
): void {
  const radius = isEvent ? GOAL_OVERLAY.TIMING_EVENT_RING_RADIUS : GOAL_OVERLAY.TIMING_RING_RADIUS;
  const lineW  = isEvent ? 4 : 3;
  const alpha  = isEvent ? 1.0 : 0.7;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth   = lineW;
  ctx.globalAlpha = alpha;
  if (isEvent) {
    ctx.shadowColor = color;
    ctx.shadowBlur  = 8;
  }
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Draw: Spatial Goal
// ---------------------------------------------------------------------------

/**
 * Spatial goals don't expose landmark names in their results, so we render a
 * pass/fail badge at a fixed offset that shifts per goal index to avoid overlap.
 * Includes annotation label for identification.
 */
function drawSpatialGoal(
  ctx: CanvasRenderingContext2D,
  _key: string,
  result: SpatialGoalResult,
  goalIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
): void {
  const color = result.passed ? '#19C37D' : '#F05D5E';
  const statusLabel = result.passed ? 'PASS' : 'FAIL';
  // Include annotation label if available (truncated)
  const annoLabel = result.annotation_label;
  const displayText = annoLabel
    ? `${statusLabel}: ${annoLabel.slice(0, 16)}`
    : statusLabel;

  // Stack badges vertically in the top-left area of the rendered video
  const bx = offsetX + 40;
  const by = offsetY + 40 + goalIndex * 34;

  ctx.save();
  ctx.font = GOAL_OVERLAY.BADGE_FONT;
  const textW = ctx.measureText(displayText).width;
  const bw = Math.max(GOAL_OVERLAY.SPATIAL_BADGE_SIZE.width, textW + 16);
  const bh = GOAL_OVERLAY.SPATIAL_BADGE_SIZE.height;

  // Clamp to visible area
  if (bx + bw > offsetX + renderWidth || by + bh > offsetY + renderHeight) { ctx.restore(); return; }

  // Background with colored left accent strip
  ctx.fillStyle = GOAL_OVERLAY.BADGE_BG;
  ctx.beginPath();
  ctx.roundRect(bx, by, bw, bh, 4);
  ctx.fill();

  // Left accent strip
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(bx, by, 4, bh, [4, 0, 0, 4]);
  ctx.fill();

  // Text
  ctx.fillStyle   = color;
  ctx.textAlign   = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayText, bx + bw / 2, by + bh / 2);
  ctx.restore();

}

// ---------------------------------------------------------------------------
// Draw: Movement Goal
// ---------------------------------------------------------------------------

/**
 * Shared primitive: draw a trajectory polyline (up to and including
 * `frameIndex`) plus a glowing dot at the current-frame position. Used by
 * `drawMovementGoal` (a scored result's `trajectory_result.trajectory_points`,
 * 4-tuples `[frame, x, y, z]`) and by the authoring-time preview path
 * (the travel-preview endpoint's `trajectory_points`, 3-tuples
 * `[frame, x, y]`) — both shapes destructure identically since only the
 * first three elements are ever read. Returns the last-drawn point's
 * canvas position (for an optional score badge), or `null` if nothing was
 * in range to draw.
 */
function drawTrajectoryPath(
  ctx: CanvasRenderingContext2D,
  points: ReadonlyArray<readonly [number, number, number, ...number[]]>,
  color: string,
  frameIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
): { x: number; y: number } | null {
  const pointsUpTo = points.filter(([f]) => f <= frameIndex);
  if (pointsUpTo.length === 0) return null;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth   = GOAL_OVERLAY.MOVEMENT_LINE_WIDTH;
  ctx.globalAlpha = 0.75;
  ctx.beginPath();

  pointsUpTo.forEach(([, px, py], i) => {
    const cx = px * renderWidth + offsetX;
    const cy = py * renderHeight + offsetY;
    if (i === 0) ctx.moveTo(cx, cy);
    else ctx.lineTo(cx, cy);
  });
  ctx.stroke();
  ctx.restore();

  // Current-position dot
  const last = pointsUpTo[pointsUpTo.length - 1];
  const dotX = last[1] * renderWidth + offsetX;
  const dotY = last[2] * renderHeight + offsetY;

  ctx.save();
  ctx.fillStyle   = color;
  ctx.shadowColor = color;
  ctx.shadowBlur  = 6;
  ctx.beginPath();
  ctx.arc(dotX, dotY, GOAL_OVERLAY.MOVEMENT_DOT_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  return { x: dotX, y: dotY };
}

function drawMovementGoal(
  ctx: CanvasRenderingContext2D,
  key: string,
  result: SpatialGoalResult,
  goalIndex: number,
  frameIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
): void {
  const trajectory = result.trajectory_result?.trajectory_points;

  if (!trajectory || trajectory.length === 0) {
    // Fall back to same pass/fail badge as spatial goal
    drawSpatialGoal(ctx, key, result, goalIndex, renderWidth, renderHeight, offsetX, offsetY);
    return;
  }

  const score = result.passed ? Math.round((result.confidence ?? 0.8) * 100) : 0;
  const color = scoreToColor(score);

  const dot = drawTrajectoryPath(ctx, trajectory, color, frameIndex, renderWidth, renderHeight, offsetX, offsetY);
  if (!dot) return;

  // Score badge near the dot
  drawScoreBadge(ctx, dot.x + 16, dot.y - 10, score);
}

/**
 * Authoring-time demo-path preview (travel/preview-path-points): the same
 * polyline+dot primitive as `drawMovementGoal`, minus the score badge
 * (there is no score yet — this draws while the coach is still picking the
 * target, before any evaluation exists).
 */
function drawPreviewTrajectory(
  ctx: CanvasRenderingContext2D,
  points: Array<[number, number, number]>,
  frameIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
): void {
  drawTrajectoryPath(
    ctx, points, GOAL_OVERLAY.PREVIEW_PATH_COLOR, frameIndex, renderWidth, renderHeight, offsetX, offsetY,
  );
}

// ---------------------------------------------------------------------------
// Draw: Joint Angle Goal
// ---------------------------------------------------------------------------

/**
 * Joint angle goal results don't include the three joint landmark names, so
 * we fall back to a pass/fail badge positioned similarly to spatial goals.
 */
function drawJointAngleGoal(
  ctx: CanvasRenderingContext2D,
  key: string,
  result: SpatialGoalResult,
  goalIndex: number,
  renderWidth: number,
  renderHeight: number,
  offsetX: number,
  offsetY: number,
): void {
  drawSpatialGoal(ctx, key, result, goalIndex, renderWidth, renderHeight, offsetX, offsetY);
}

// ---------------------------------------------------------------------------
// Utility: get per-frame value using valid_frame_indices mapping
// ---------------------------------------------------------------------------

/**
 * Given parallel arrays `perFrameValues` and `validFrameIndices` (as returned by
 * the backend for axis/timing goals), find the value corresponding to `frameIndex`.
 * Returns null if no matching frame exists.
 */
function getPerFrameValue(
  perFrameValues: number[] | undefined,
  validFrameIndices: number[] | undefined,
  frameIndex: number,
): number | null {
  if (!perFrameValues || !validFrameIndices) return null;

  const idx = validFrameIndices.indexOf(frameIndex);
  if (idx === -1) return null;

  const value = perFrameValues[idx];
  return value !== undefined ? value : null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function GoalOverlayLayer({
  goalResults,
  poseData,
  currentFrame,
  width,
  height,
  mirrored,
  enabled,
  hideScores = false,
  videoElement,
  previewTrajectory,
}: GoalOverlayLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Always sync canvas dimensions
    canvas.width  = width;
    canvas.height = height;

    // Clear on every render cycle
    ctx.clearRect(0, 0, width, height);

    if (!enabled || width === 0 || height === 0) return;

    // Compute letterbox-aware render rect so goal indicators align with
    // the actual rendered video area rather than the full container box.
    const { offsetX, offsetY, renderWidth, renderHeight } = computeVideoRenderRect(
      width,
      height,
      videoElement,
    );

    // Demo-path preview (travel/preview-path-points): authoring-time data,
    // independent of goalResults/hideScores -- a coach picking a travel
    // target sees the demo's own path drawn as soon as a preview computes.
    if (previewTrajectory && previewTrajectory.length > 0) {
      drawPreviewTrajectory(ctx, previewTrajectory, currentFrame, renderWidth, renderHeight, offsetX, offsetY);
    }

    if (!goalResults) return;

    // Track badge index counters for goal types that use offset positioning
    let spatialIndex   = 0;
    let movementIndex  = 0;
    let jointAngleIndex = 0;

    const entries = Object.entries(goalResults);

    entries.forEach(([key, result]) => {
      if (!result) return;

      // Viewpoint observability Phase 1: a WITHHELD checkpoint (the evaluator
      // abstained) has no measurement, so there is no verdict to paint. Every
      // overlay here colours itself pass/fail from a score, so drawing one
      // would put a red "you failed" mark on something that was never
      // measured. One guard for all five goal types — `status` is absent on
      // every pre-contract evaluation, which reads as "computed".
      if (result.status === 'abstained') return;

      const goalType = classifyGoalKey(key);

      try {
        switch (goalType) {
          case 'axis':
            if (isAxisGoalResult(result)) {
              drawAxisGoal(ctx, key, result, poseData, currentFrame, renderWidth, renderHeight, offsetX, offsetY, mirrored, hideScores);
            } else {
              console.warn(`[GoalOverlayLayer] Key "${key}" classified as axis but failed isAxisGoalResult`, result);
            }
            break;

          case 'timing':
            if (isTimingGoalResult(result)) {
              drawTimingGoal(ctx, key, result, poseData, currentFrame, renderWidth, renderHeight, offsetX, offsetY, mirrored, hideScores);
            }
            break;

          case 'spatial':
            // Spatial goals are pass/fail badges with no geometric value on the
            // template video — skip them entirely when hiding scores.
            if (!hideScores) {
              drawSpatialGoal(ctx, key, result as SpatialGoalResult, spatialIndex++, renderWidth, renderHeight, offsetX, offsetY);
            }
            break;

          case 'movement':
            // Movement trajectory goals are athlete-specific — skip on template video.
            if (!hideScores) {
              drawMovementGoal(
                ctx,
                key,
                result as SpatialGoalResult,
                movementIndex++,
                currentFrame,
                renderWidth,
                renderHeight,
                offsetX,
                offsetY,
              );
            }
            break;

          case 'joint_angle':
            // Joint angle goals are pass/fail badges with no geometric value on the
            // template video — skip them entirely when hiding scores.
            if (!hideScores) {
              drawJointAngleGoal(
                ctx,
                key,
                result as SpatialGoalResult,
                jointAngleIndex++,
                renderWidth,
                renderHeight,
                offsetX,
                offsetY,
              );
            }
            break;
        }
      } catch (err) {
        // Guard against per-goal drawing errors so a single bad result doesn't
        // blank the whole overlay.
        console.error(`[GoalOverlayLayer] Error drawing goal "${key}":`, err);
      }
    });
  }, [currentFrame, goalResults, poseData, width, height, mirrored, enabled, hideScores, videoElement, previewTrajectory]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none z-20"
      style={{ width: '100%', height: '100%' }}
      aria-label="Goal overlay"
    />
  );
}
